import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "../../components/toast";
import styles from "../../styles/__login.module.scss";
import Footer from "../../components/footer/Footer";
import { APP_PATH } from "../../api/backendPath";
import CountryCodeSelect from "../../components/country-code-select/CountryCodeSelect";
import {
  DEFAULT_COUNTRY_ISO,
  findCountryByIso,
} from "../../constants/countries";
import {
  getMobileLengthIssue,
  limitMobileDigits,
  mobileLengthErrorText,
} from "../../utils/phoneValidation";

const ForgotPassword = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Step management: 1 = send OTP, 2 = verify OTP, 3 = reset password
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Step 1 fields
  const [countryIso, setCountryIso] = useState(DEFAULT_COUNTRY_ISO);
  const [mobileNumber, setMobileNumber] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [generatedCaptcha, setGeneratedCaptcha] = useState(generateCaptcha());

  // Step 2 fields
  const [otp, setOtp] = useState("");

  // Step 3 fields
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errors, setErrors] = useState({});
  // Set by the first "Submit"; from then on a too-short mobile number is flagged as it changes.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Same country list (with search and flags) as every other mobile number field in the apps.
  const selectedCountryData = findCountryByIso(countryIso);
  // Digit count required for the selected country (libphonenumber-js metadata).
  const mobileLengthIssue = getMobileLengthIssue(mobileNumber, countryIso);
  const mobileLengthError = mobileLengthIssue
    ? mobileLengthErrorText(t, i18n.language, selectedCountryData, mobileLengthIssue)
    : "";
  // Too long at once (e.g. after switching country), too short once the form was submitted;
  // otherwise the message from the last attempt ("required").
  const mobileError = mobileLengthIssue
    ? submitAttempted || mobileLengthIssue.tooLong
      ? mobileLengthError
      : ""
    : errors.mobile;
  const clearMobileError = () =>
    setErrors((prev) => (prev.mobile ? { ...prev, mobile: undefined } : prev));

  function generateCaptcha() {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    return Array.from(
      { length: 6 },
      () => chars[Math.floor(Math.random() * chars.length)],
    ).join("");
  }

  // Countdown for the "Resend OTP" link (2 minutes). The link is hidden while the
  // timer is running and re-appears only after it reaches zero.
  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const id = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(id);
  }, [resendTimer]);

  const formatTimer = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const fullMobileNumber = `${selectedCountryData.code}${mobileNumber}`;

  // STEP 1: Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    const newErrors = {};

    if (!mobileNumber.trim()) {
      newErrors.mobile = "Please enter your mobile number.";
    } else if (mobileLengthIssue) {
      newErrors.mobile = mobileLengthError;
    }

    if (!captchaInput.trim()) {
      newErrors.captcha = "Please enter captcha.";
    } else if (captchaInput !== generatedCaptcha) {
      newErrors.captcha = "Captcha does not match.";
      setGeneratedCaptcha(generateCaptcha());
      setCaptchaInput("");
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APP_PATH.forgotPasswordSendOtp,
        {
          mobile_number: fullMobileNumber,
        },
      );

      if (response.data.status === true) {
        toast.success("OTP sent successfully to your mobile number");
        setStep(2);
        setResendTimer(120);
      } else {
        toast.error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Failed to send OTP. Please try again.";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!otp.trim()) {
      newErrors.otp = "Please enter the OTP.";
    } else if (otp.length < 4) {
      newErrors.otp = "OTP must be at least 4 digits.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APP_PATH.forgotPasswordVerifyOtp,
        {
          mobile_number: fullMobileNumber,
          otp: otp,
        },
      );

      if (response.data.status === true) {
        toast.success("OTP verified successfully");
        setStep(3);
      } else {
        toast.error(response.data.message || "Invalid OTP");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "OTP verification failed. Please try again.";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!newPassword.trim()) {
      newErrors.newPassword = "Please enter a new password.";
    } else if (newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters.";
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your password.";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APP_PATH.forgotPasswordReset,
        {
          mobile_number: fullMobileNumber,
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
      );

      if (response.data.status === true) {
        toast.success(
          "Password reset successfully! Please login with your new password.",
        );
        setTimeout(() => navigate("/"), 2000);
      } else {
        toast.error(response.data.message || "Failed to reset password");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Failed to reset password. Please try again.";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APP_PATH.forgotPasswordSendOtp,
        {
          mobile_number: fullMobileNumber,
        },
      );
      if (response.data.status === true) {
        toast.success("OTP resent successfully");
        setResendTimer(120);
      } else {
        toast.error(response.data.message || "Failed to resend OTP");
      }
    } catch (error) {
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles["main-container"]}>
      <main className={styles["main-content"]}>
        <div className={styles["content-wrapper"]}>
          {/* Illustration */}
          <div className={styles["illustration"]}>
            <div className={styles["doctor-illustration"]}>
              <img
                src="/ForgotPassword.png"
                alt="Forgot password illustration"
                className={styles["doctor-image"]}
              />
            </div>
          </div>

          {/* Form */}
          <div className={styles["login-form-container"]}>
            <h2>{t("forgotPassword") || "Forgot Password"}</h2>

            {/* STEP 1: Send OTP */}
            {step === 1 && (
              <form onSubmit={handleSendOtp} className={styles["login-form"]}>
                {/* Mobile Number */}
                <div className={styles["form-group"]}>
                  <label htmlFor="mobile">
                    {t("mobileNumber") || "Mobile Number"}
                  </label>
                  <div className={styles["mobile-input-container"]}>
                    <CountryCodeSelect
                      className={styles["country-selector"]}
                      value={countryIso}
                      onChange={(country) => {
                        setCountryIso(country.iso);
                        clearMobileError();
                      }}
                    />
                    <input
                      type="tel"
                      id="mobile"
                      placeholder="770 123 4567"
                      inputMode="numeric"
                      value={mobileNumber}
                      onChange={(e) => {
                        // Digits only, no leading 0, and no more digits than the
                        // selected country's mobile numbers have.
                        setMobileNumber(limitMobileDigits(e.target.value, countryIso));
                        clearMobileError();
                      }}
                      aria-invalid={!!mobileError || undefined}
                      aria-describedby={mobileError ? "mobile-error" : undefined}
                      className={`${styles["phone-input"]} ${mobileError ? styles["input-error"] : ""}`}
                    />
                  </div>
                  {mobileError && (
                    <p id="mobile-error" role="alert" className={styles["rule-invalid"]}>
                      {mobileError}
                    </p>
                  )}
                </div>

                {/* Captcha */}
                <div className={styles["form-group"]}>
                  <label htmlFor="captcha">
                    {t("captchaVerification", "Captcha Verification")}
                  </label>
                  <div className={styles["capcha-box"]}>
                    <span className={styles["captcha-txt"]}>
                      {generatedCaptcha}
                    </span>
                    <button
                      onClick={() => setGeneratedCaptcha(generateCaptcha())}
                      type="button"
                      className={styles["change-capcha"]}
                    >
                      <svg
                        stroke="currentColor"
                        fill="currentColor"
                        strokeWidth="0"
                        viewBox="0 0 512 512"
                        height="20px"
                        width="20px"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill="none"
                          strokeLinecap="round"
                          strokeMiterlimit="10"
                          strokeWidth="32"
                          d="m400 148-21.12-24.57A191.43 191.43 0 0 0 240 64C134 64 48 150 48 256s86 192 192 192a192.09 192.09 0 0 0 181.07-128"
                        ></path>
                        <path d="M464 97.42V208a16 16 0 0 1-16 16H337.42c-14.26 0-21.4-17.23-11.32-27.31L436.69 86.1C446.77 76 464 83.16 464 97.42z"></path>
                      </svg>
                    </button>
                  </div>
                  <input
                    type="text"
                    id="captcha"
                    placeholder="Enter captcha"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    className={`${styles["form-input"]} ${errors.captcha ? styles["input-error"] : ""}`}
                  />
                  {errors.captcha && (
                    <p className={styles["rule-invalid"]}>{errors.captcha}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className={styles["login-btn"]}
                  disabled={isLoading}
                >
                  {isLoading ? "Sending..." : t("submit") || "Submit"}
                </button>
              </form>
            )}

            {/* STEP 2: Verify OTP */}
            {step === 2 && (
              <form onSubmit={handleVerifyOtp} className={styles["login-form"]}>
                <p
                  style={{
                    marginBottom: "16px",
                    color: "#666",
                    fontSize: "14px",
                  }}
                >
                  An OTP has been sent to <strong>{fullMobileNumber}</strong>{" "}
                  via WhatsApp/SMS.
                </p>

                <div className={styles["form-group"]}>
                  <label htmlFor="otp">{t("enterOtp", "Enter OTP")}</label>
                  <input
                    type="text"
                    id="otp"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className={`${styles["form-input"]} ${errors.otp ? styles["input-error"] : ""}`}
                    maxLength={6}
                  />
                  {errors.otp && (
                    <p className={styles["rule-invalid"]}>{errors.otp}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className={styles["login-btn"]}
                  disabled={isLoading}
                >
                  {isLoading ? "Verifying..." : t("verifyOtp", "Verify OTP")}
                </button>

                <p
                  style={{
                    marginTop: "12px",
                    textAlign: "center",
                    fontSize: "14px",
                  }}
                >
                  {resendTimer > 0 ? (
                    <span style={{ color: "#666" }}>
                      {t("resendCodeIn", "Resend code in")}{" "}
                      {formatTimer(resendTimer)}
                    </span>
                  ) : (
                    <>
                      {t("didntReceiveCode", "Didn't receive the code?") + " "}
                      <span
                        onClick={isLoading ? undefined : handleResendOtp}
                        style={{
                          color: "#0DD0D0",
                          cursor: isLoading ? "not-allowed" : "pointer",
                          fontWeight: "600",
                          opacity: isLoading ? 0.6 : 1,
                        }}
                      >
                        {t("resendOtp", "Resend OTP")}
                      </span>
                    </>
                  )}
                </p>

                <p
                  style={{
                    marginTop: "8px",
                    textAlign: "center",
                    fontSize: "13px",
                  }}
                >
                  <span
                    onClick={() => {
                      setStep(1);
                      setOtp("");
                      setErrors({});
                    }}
                    style={{
                      color: "#666",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    ← Back to mobile number
                  </span>
                </p>
              </form>
            )}

            {/* STEP 3: Reset Password */}
            {step === 3 && (
              <form
                onSubmit={handleResetPassword}
                className={styles["login-form"]}
              >
                <p
                  style={{
                    marginBottom: "16px",
                    color: "#666",
                    fontSize: "14px",
                  }}
                >
                  OTP verified. Please set your new password.
                </p>

                <div className={styles["form-group"]}>
                  <label htmlFor="newPassword">
                    {t("newPassword") || "New Password"}
                  </label>
                  <div className={styles["password-wrapper"]}>
                    <input
                      type={showNewPassword ? "text" : "password"}
                      id="newPassword"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className={`${styles["form-input"]} ${errors.newPassword ? styles["input-error"] : ""}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className={styles["password-toggle"]}
                    >
                      {showNewPassword ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </button>
                  </div>
                  {errors.newPassword && (
                    <p className={styles["rule-invalid"]}>
                      {errors.newPassword}
                    </p>
                  )}
                </div>

                <div className={styles["form-group"]}>
                  <label htmlFor="confirmPassword">
                    {t("confirmPassword") || "Confirm Password"}
                  </label>
                  <div className={styles["password-wrapper"]}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`${styles["form-input"]} ${errors.confirmPassword ? styles["input-error"] : ""}`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className={styles["password-toggle"]}
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className={styles["rule-invalid"]}>
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  className={styles["login-btn"]}
                  disabled={isLoading}
                >
                  {isLoading
                    ? "Resetting..."
                    : t("resetPassword") || "Reset Password"}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ForgotPassword;
